import { environment } from "./environment";

/** `none` also covers a coleta that predates the feature and has no code row. */
export type AccessCodeStatus = "none" | "active" | "locked" | "expired";

/**
 * Which share this respondent needs. Resolved server-side at call time from
 * whether they have an auth account — never inferred from the code row, which
 * can outlive a registration.
 */
export type AccessMode = "code" | "login";

/** The reveal endpoint's payload — the only place a code value crosses the wire. */
export interface IAccessCodeReveal {
  respondentName: string | null;
  instrumentTitle: string;
  accessMode: AccessMode;
  /** Present only in `code` mode. */
  code: string | null;
  /** Present only in `code` mode; the code screen's link is built from it. */
  invitationUid: string | null;
  status: AccessCodeStatus;
}

/**
 * The auth-fe landing page for a CODE share — byte-identical to what the
 * accessCodes e-mail builds (auth-service `email-repository.sendAccessCodes`).
 * This text is that e-mail sent by hand, so it must land the recipient on the
 * same page in the same state; from there they pick "Entrar com código".
 *
 * Both params are load-bearing:
 * - `hasCode=1` is what tells auth-fe this reader is holding a code, so the page
 *   leads with the code option instead of the registration form. Only the
 *   access-code e-mail sets it; the plain invite deliberately does not.
 * - `inviteType=RESPONDENT` keeps the applicator branch from claiming this link.
 *
 * Code mode only — never build a login-mode share from this.
 */
export function buildCodeShareUrl(invitationUid: string): string {
  const query = new URLSearchParams({
    code: invitationUid,
    inviteType: "RESPONDENT",
    hasCode: "1",
  });
  return `${environment.auth.baseUrl}/accept-invitation?${query.toString()}`;
}

/**
 * The WhatsApp-style text the psychologist sends.
 *
 * In `login` mode it carries the plain platform URL and **no code line, ever** —
 * that respondent has a password, and a code for them would be a password
 * bypass (the backend refuses to issue one, so there is nothing to leak here,
 * but the text must not imply otherwise either).
 *
 * @throws Error when a code-mode payload is missing the pieces its text needs,
 * so a half-composed share is never copied silently.
 */
export function composeShareText(reveal: IAccessCodeReveal): string {
  const greeting = reveal.respondentName
    ? `Olá, ${reveal.respondentName}!`
    : "Olá!";

  if (reveal.accessMode === "login") {
    return [
      greeting,
      "",
      `Você tem um instrumento para responder na Catvante: ${reveal.instrumentTitle}.`,
      "Entre com seu e-mail e senha:",
      environment.instrumentsFrontendBaseUrl,
    ].join("\n");
  }

  if (!reveal.code || !reveal.invitationUid) {
    throw new Error(
      "Não foi possível montar o texto: o código desta atribuição não está disponível."
    );
  }

  return [
    greeting,
    "",
    `Você tem um instrumento para responder na Catvante: ${reveal.instrumentTitle}.`,
    'Você não precisa criar uma conta — abra o link, escolha "Entrar com código" e digite o código abaixo:',
    "",
    buildCodeShareUrl(reveal.invitationUid),
    "",
    `Código: ${reveal.code}`,
  ].join("\n");
}

/** Chip label/colour for the listing's `accessCodeStatus`. */
export function describeAccessCodeStatus(
  status: AccessCodeStatus
): { label: string; color: "success" | "warning" | "error" | "default" } {
  switch (status) {
    case "active":
      return { label: "Código ativo", color: "success" };
    case "locked":
      return { label: "Código bloqueado", color: "error" };
    case "expired":
      return { label: "Aplicação expirada", color: "warning" };
    default:
      return { label: "Sem código", color: "default" };
  }
}
